export type FhirObject = Record<string, unknown>;

export const FHIR_DETAIL_SEPARATOR = ', ';

export function asObject(value: unknown): FhirObject | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as FhirObject : undefined;
}

export function isObject(value: FhirObject | undefined): value is FhirObject {
  return value !== undefined;
}

export function readString(object: FhirObject | undefined, property: string): string | undefined {
  const value = object?.[property];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

export function readArray(object: FhirObject | undefined, property: string): unknown[] {
  const value = object?.[property];
  return Array.isArray(value) ? value : [];
}

export function readObjects(object: FhirObject | undefined, property: string): FhirObject[] {
  return readArray(object, property).map(asObject).filter(isObject);
}

export function readStringArray(object: FhirObject, property: string): string[] {
  return readArray(object, property).filter((value): value is string => typeof value === 'string');
}

export function readResourceType(resource: FhirObject): string | undefined {
  return readString(resource, 'resourceType');
}

export function readConcept(object: FhirObject | undefined, property: string): string | undefined {
  return readConceptText(asObject(object?.[property]));
}

export function readConceptText(concept: FhirObject | undefined): string | undefined {
  if (!concept) {
    return undefined;
  }
  const coding = readObjects(concept, 'coding')[0];
  return readString(concept, 'text') || readString(coding, 'display') || readString(coding, 'code');
}

export function readConcepts(object: FhirObject | undefined, property: string): string[] {
  return readObjects(object, property).map(readConceptText).filter(isText);
}

export function readReference(object: FhirObject | undefined, property: string): string | undefined {
  return readString(asObject(object?.[property]), 'reference');
}

export function readReferenceDisplay(object: FhirObject | undefined, property: string): string | undefined {
  return readString(asObject(object?.[property]), 'display');
}

export function readNotes(resource: FhirObject): string | undefined {
  return joinDetails(readObjects(resource, 'note').map(note => readString(note, 'text')));
}

export function readQuantity(quantity: FhirObject | undefined): string | undefined {
  const value = quantity?.['value'];
  if (typeof value !== 'number' && typeof value !== 'string') {
    return undefined;
  }
  return [String(value), readString(quantity, 'unit') || readString(quantity, 'code')].filter(isText).join(' ');
}

export function readDate(value: string | undefined): string | undefined {
  return value ? value.slice(0, 10) : undefined;
}

export function joinDetails(parts: (string | undefined)[]): string | undefined {
  return parts.filter(isText).join(FHIR_DETAIL_SEPARATOR) || undefined;
}

export function isText(value: string | undefined): value is string {
  return value !== undefined && value.length > 0;
}
